/* eslint-disable multiline-comment-style */
import isUndefined from 'lodash/isUndefined';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { AvailabilitySimple } from '@dreryk/egabinet-appointment-frontend-api';
import { DoctorSimple } from '@dreryk/egabinet-personnel-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';

import {
   Timetable,
   TimetableEvent,
   TimetableEventBackgroundConfiguration,
   TimetableSlotSelectionEvent,
   useTimetableState,
} from '@dreryk/react-calendar';
import { Bean, getCurrentTimezone } from '@dreryk/react-common';
import {
   CalendarLegacy,
   Header,
   StyledFlex,
   StyledMargin,
   StyledMaxWidth,
   useCalendarRangeSelectorLegacy,
   useCalendarStateLegacy,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { personnelTranslationKeys } from '../translations';

import { AvailabilityCreationModal } from './availabilityCreation/AvailabilityCreationModal';
import { AvailabilitySlot } from './AvailabilitySlot';
import { AvailabilityUpdateModal } from './availabilityUpdate/AvailabilityUpdateModal';
import { DoctorSelect } from './DoctorSelect';
import { useAvailabilitiesSearch, useSelectedDoctorState, useUpdateModal } from './SchedulePanel.hooks';
import {
   convertAvailability,
   handleSelectionConstraint,
   selectionOnlyWithinOneDay,
   slotLabelStyleConfiguration,
} from './SchedulePanel.utils';
import { ScheduleTimetableActions } from './actions/ScheduleTimetableActions';
import { useWeekendsVisibilityPreference } from './actions/ScheduleTimetableActions.hooks';

const SLOT_DURATION_MINUTES = 30;

const SchedulePanelContainer = styled.div`
   display: flex;
   flex: 1;
   overflow: hidden;
`;
SchedulePanelContainer.displayName = 'SchedulePanelContainer';

const HeaderWithMargin = styled(Header)`
   margin-top: 7px;
   margin-bottom: 17px;
`;
HeaderWithMargin.displayName = 'HeaderWithMargin';

type SchedulePanelProps = {
   readonly doctors: DoctorSimple[];
   readonly defaultDoctor: DoctorSimple | null;
};

const eventBackgroundConfiguration: TimetableEventBackgroundConfiguration<
   Bean,
   'secondaryLighter' | 'borderLighter'
> = {
   backgroundColors: ['secondaryLighter', 'borderLighter'],
   backgroundColorSelector: (event) => {
      return new Date() < event.end ? 'secondaryLighter' : 'borderLighter';
   },
};

// eslint-disable-next-line max-lines-per-function
export const SchedulePanel = (props: SchedulePanelProps): JSX.Element => {
   const { selectedDate, setSelectedDate } = useCalendarStateLegacy();
   const [selectedDoctor, setSelectedDoctor] = useSelectedDoctorState(props.defaultDoctor, props.doctors);
   const [preferWeekendsVisible] = useWeekendsVisibilityPreference();

   const { dateRange } = useTimetableState(selectedDate, Boolean(preferWeekendsVisible));
   const calendarRangeSelector = useCalendarRangeSelectorLegacy(dateRange);

   const [selectedSlot, setSelectedSlot] = useState<TimetableSlotSelectionEvent<DoctorSimple>>();

   const {
      data: availabilities,
      refetch: refetchAvailabilities,
      fetching: availabilitiesFetching,
   } = useAvailabilitiesSearch(dateRange, PractitionerId.of(selectedDoctor.practitionerId));
   const events = useMemo(() => availabilities?.map(convertAvailability) || [], [availabilities]);
   const { onEventClick, closeModal: closeUpdateModal, selectedAvailability } = useUpdateModal();

   const eventContentRenderer = useCallback(
      (event: TimetableEvent<AvailabilitySimple>) => <AvailabilitySlot event={event} />,
      []
   );

   const handleSlotSelection = (selectionEvent: TimetableSlotSelectionEvent<DoctorSimple>): void => {
      setSelectedSlot(selectionEvent);
   };

   const handleCreationClose = (): void => {
      setSelectedSlot(undefined);
   };

   return (
      <SchedulePanelContainer>
         {!isUndefined(selectedSlot) && (
            <AvailabilityCreationModal
               selectedSlot={selectedSlot}
               onClose={handleCreationClose}
               refetch={refetchAvailabilities}
            />
         )}
         {!isUndefined(selectedAvailability) && (
            <AvailabilityUpdateModal
               selectedEvent={selectedAvailability}
               onClose={closeUpdateModal}
               refetch={refetchAvailabilities}
            />
         )}
         <StyledMaxWidth maxWidth={255}>
            <StyledFlex direction={'column'} gap={'3px'}>
               <StyledMargin left={25} right={25}>
                  <HeaderWithMargin testId={'schedule'}>{personnelTranslationKeys.schedule}</HeaderWithMargin>
                  {props.doctors.length === 1
                     ? null
                     : protectFeature(
                          'searchingAllPractitioners',
                          <DoctorSelect
                             onChange={setSelectedDoctor}
                             selectedDoctor={selectedDoctor}
                             doctors={props.doctors}
                          />,
                          null
                       )}
               </StyledMargin>
               <CalendarLegacy
                  date={selectedDate}
                  onDateChange={setSelectedDate}
                  isDayHighlighted={calendarRangeSelector}
                  testId={'scheduleCalendar'}
               />
            </StyledFlex>
         </StyledMaxWidth>
         <Timetable<DoctorSimple, AvailabilitySimple, never>
            view={'timeGridWeek'}
            resource={selectedDoctor}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            preferWeekendsVisible={preferWeekendsVisible}
            autoScrollToCurrentTime
            eventBackgroundConfiguration={eventBackgroundConfiguration}
            events={events}
            eventContentRenderer={eventContentRenderer}
            onSlotSelection={handleSlotSelection}
            onEventClick={onEventClick}
            selectionOverlapping={false}
            allowsSelection={selectionOnlyWithinOneDay}
            selectionConstraint={handleSelectionConstraint()}
            slotDurationInMinutes={SLOT_DURATION_MINUTES}
            slotLabelStyleConfiguration={slotLabelStyleConfiguration}
            testId={'timetable'}
            interactive={!availabilitiesFetching}
            timeZone={getCurrentTimezone()}
            actions={<ScheduleTimetableActions selectedDate={selectedDate} setSelectedDate={setSelectedDate} />}
         />
      </SchedulePanelContainer>
   );
};
